import { useEffect, useState, useCallback } from 'react'
import { MapPin, Crosshair, Users, Clock, Wifi, WifiOff } from 'lucide-react'
import { useFamily } from '../hooks/useFamily'
import { motion } from 'framer-motion'
import { MobileMap } from '../components/MobileComponents'

const Locator = () => {
  const { familyMembers } = useFamily()
  const [myLocation, setMyLocation] = useState(null)
  const [error, setError] = useState(null)
  const [watchId, setWatchId] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [lastUpdate, setLastUpdate] = useState(null)
  const [accuracy, setAccuracy] = useState(null)

  const requestLocation = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setError('Geolocalização não suportada neste navegador.')
      return
    }

    setIsLoading(true)
    setError(null)

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords
        const location = { 
          lat: latitude, 
          lng: longitude, 
          timestamp: Date.now(),
          accuracy: accuracy
        }
        setMyLocation(location)
        setAccuracy(accuracy)
        setLastUpdate(new Date())
        setIsLoading(false)
      },
      (err) => {
        setError(`Erro: ${err.message}`)
        setIsLoading(false)
      },
      { 
        enableHighAccuracy: true, 
        timeout: 15000,
        maximumAge: 30000
      }
    )
  }, [])

  const startWatching = useCallback(() => {
    if (!('geolocation' in navigator)) return
    
    setIsLoading(true)
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords
        const location = { 
          lat: latitude, 
          lng: longitude, 
          timestamp: Date.now(),
          accuracy: accuracy
        }
        setMyLocation(location)
        setAccuracy(accuracy)
        setLastUpdate(new Date())
        setIsLoading(false)
      },
      (err) => {
        setError(`Erro: ${err.message}`)
        setIsLoading(false)
      },
      { 
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 10000
      }
    )
    setWatchId(id)
  }, [])

  const stopWatching = useCallback(() => {
    if (watchId && 'geolocation' in navigator) {
      navigator.geolocation.clearWatch(watchId)
      setWatchId(null)
    }
  }, [watchId])

  const getLocationAccuracy = (acc) => {
    if (acc <= 10) return { text: 'Muito precisa', color: 'text-green-600', icon: Wifi }
    if (acc <= 50) return { text: 'Precisa', color: 'text-yellow-600', icon: Wifi }
    return { text: 'Aproximada', color: 'text-red-600', icon: WifiOff }
  }

  const formatDistance = (meters) => {
    if (meters < 1000) return `${Math.round(meters)}m`
    return `${(meters / 1000).toFixed(1)}km`
  }

  useEffect(() => {
    requestLocation()
    return () => stopWatching()
  }, [requestLocation, stopWatching])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Localizador</h1>
          <p className="text-neutral-600 mt-1">Visualize sua posição e dos membros da família</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={requestLocation} 
            disabled={isLoading}
            className="btn-secondary flex items-center gap-2 disabled:opacity-50"
          >
            <Crosshair className="h-4 w-4" />
            {isLoading ? 'Obtendo...' : 'Atualizar posição'}
          </button>
          {watchId ? (
            <button onClick={stopWatching} className="btn-secondary">Parar acompanhamento</button>
          ) : (
            <button onClick={startWatching} disabled={isLoading} className="btn-primary disabled:opacity-50">
              Iniciar acompanhamento
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-neutral-200 p-4 lg:col-span-3"
        >
          <h2 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2 text-sm">
            <MapPin className="h-4 w-4 text-primary-500" />
            Mapa Interativo
          </h2>
          {myLocation ? (
            <MobileMap
              center={[myLocation.lat, myLocation.lng]}
              zoom={15}
              markers={[{
                id: 'user-location',
                lat: myLocation.lat,
                lng: myLocation.lng,
                name: 'Sua posição atual',
                description: `Precisão: ±${formatDistance(accuracy || 0)}`
              }]}
              height="320px"
            />
          ) : (
            <div className="h-80 bg-neutral-100 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <MapPin className="h-8 w-8 mx-auto text-neutral-400" />
                <div>Clique em "Atualizar posição" para ver o mapa</div>
              </div>
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-neutral-200 p-4"
        >
          <h2 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2 text-sm">
            <Users className="h-4 w-4 text-primary-500" />
            Membros da Família ({familyMembers?.length || 0})
          </h2>
          <div className="space-y-2">
            {familyMembers && familyMembers.length > 0 ? (
              (familyMembers || []).map((m) => (
                <div key={m.id} className="p-2 rounded-lg bg-neutral-50 border border-neutral-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-neutral-900 text-sm">{m.full_name || m.name}</div>
                      <div className="text-xs text-neutral-500 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Última atualização: agora (demo)
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-neutral-500">Status</div>
                      <div className="text-xs text-green-600 font-medium">Online</div>
                    </div>
                  </div>
                  <div className="mt-1 text-xs text-neutral-500">
                    📍 Demo (será integrada com GPS real)
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-3 text-neutral-500">
                <Users className="h-6 w-6 mx-auto mb-1 text-neutral-400" />
                <div className="text-xs">Nenhum membro encontrado</div>
                <div className="text-xs">Adicione membros à família primeiro</div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default Locator
